/**
 * Kiest het render-transport op basis van tenant.config.js en biedt daarbovenop
 * de dingen die voor elk transport gelijk zijn:
 *
 *   - de request-string opbouwen (11 velden, of 17 als de tenant kwaliteits-
 *     tiers gebruikt)
 *   - cache-first: bestaat de render al, dan meteen tonen zonder een verzoek
 *   - progressief renderen: eerst een snelle preview, daarna stil de HQ-versie
 *
 * Zit een tenant nog op de PHP-wachtrij, dan is 'transport' het enige dat in
 * zijn config anders staat. De rest van de configurator merkt er niets van.
 */
import tenant from '../tenant.config.js';
import { createSignalRTransport } from './signalr.js';
import { createPhpTransport } from './php.js';

const transport = tenant.render.transport === 'php'
    ? createPhpTransport(tenant)
    : createSignalRTransport(tenant);

/**
 * Veiligheidsklep. Draait de configurator op localhost, dan gaat er standaard
 * geen render-aanvraag de deur uit: die zou op de echte worker van de klant
 * belanden en daar de wachtrij vullen terwijl er misschien productiewerk loopt.
 *
 * Wil je lokaal wél tegen de echte renderer aan, zet dan bewust
 * ?echte-renders=ja in de URL. Dat is een besluit, geen bijverschijnsel.
 */
const LOKAAL = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
const ECHTE_RENDERS = typeof location !== 'undefined'
    && new URLSearchParams(location.search).get('echte-renders') === 'ja';
// Een demo-build wijst per definitie naar de TESTHUB onder een eigen tenant-naam.
// Daar hoeft de klep niet dicht: hij beschermt de productie-configuratie tegen
// een lokale sessie, niet het proefdraaien.
const DEMO = !!tenant.demoStand;
const GEBLOKKEERD = LOKAAL && !ECHTE_RENDERS && !DEMO;

if (GEBLOKKEERD) {
    console.warn(
        '[render] Lokale sessie op een PRODUCTIE-configuratie: aanvragen gaan NIET '
        + 'de deur uit, zodat de worker van %s niet geraakt wordt. Wil je dat toch, '
        + 'zet ?echte-renders=ja achter de URL. Een demo-build heeft dit niet nodig.',
        tenant.id
    );
}

export const tiers = tenant.render.tiers || null;

/** Heeft deze tenant een aparte HQ-render, of is er maar één kwaliteit? */
export const hasQualityTiers = !!(tiers && tiers.fast && tiers.hq);

/**
 * Bouw de request-string.
 *
 * Basis (altijd, 11 velden):
 *   houseType_designType_color_covering_widthCm_depthCm_led_left_right_front_viewMode
 *
 * Met kwaliteits-tiers komen daar 6 velden achter die de renderer gebruikt voor
 * beeldverhouding en sampling:
 *   _aspectX_aspectY_screenshot_width_height_samples
 */
export function buildRequestString(parts, context = 'fast') {
    const base = [
        parts.houseType, parts.designType, parts.color, parts.covering,
        parts.widthCm, parts.depthCm, parts.led,
        parts.leftWall, parts.rightWall, parts.frontWall, parts.viewMode,
    ].join('_');
    if (!hasQualityTiers) return base;
    const q = tiers[context] || tiers.fast;
    return `${base}_1_1_0_${q.width}_${q.height}_${q.samples}`;
}

/**
 * Eén scene, twee merken.
 *
 * De renderer kan het logo in de scene per render omwisselen. Welk merk dat
 * moet zijn geeft de frontend mee als voorvoegsel: `<merk>|<parameters>`.
 * Cloudrenderer haalt dat er weer af voordat de scene gebouwd wordt.
 *
 * Het merk moet óók in de bestandsnaam terugkomen, anders schrijven twee merken
 * met dezelfde configuratie over elkaars beeld heen en krijgt de een het logo
 * van de ander. Vandaar `Render_<merk>_<parameters>.jpg`.
 *
 * Staat er geen merk in de config, dan verandert er niets: geen voorvoegsel,
 * geen merk in de naam, precies zoals het nu live draait.
 */
const MERK = tenant.render.brand || null;

/** Wat er als Raw naar de hub gaat. */
function metMerk(requestString) {
    return MERK ? `${MERK}|${requestString}` : requestString;
}

/** De naam waaronder het resultaat terugkomt en waaronder het beeld te vinden is. */
function bestandssleutel(requestString) {
    return MERK ? `${MERK}_${requestString}` : requestString;
}

/**
 * Vraag een render aan en meld hem terug zodra hij klaar is.
 *
 * Cache-first: we proberen de jpg eerst rechtstreeks. Staat hij er al, dan is
 * er geen verzoek nodig en is het beeld er direct.
 */
export function requestRender(requestString, opts = {}) {
    const { onReady, onTimeout, onProgress, priority, skipCache } = opts;
    const sleutel = bestandssleutel(requestString);
    const url = transport.urlFor(sleutel);

    // Lokaal gaat er niets naar buiten, ook de cache-check niet. Die zou een
    // GET op de host van de klant zijn en dat hoort een lokale sessie niet te doen.
    if (GEBLOKKEERD) {
        console.info('[render] geblokkeerd (lokaal):', requestString);
        onTimeout && setTimeout(() => onTimeout(requestString), 1200);
        return;
    }

    // sendAs = wat de renderer krijgt (met merk-voorvoegsel)
    // fileKey = onder welke naam het resultaat terugkomt (met merk in de naam)
    // Beide vallen samen met requestString zodra er geen merk is ingesteld.
    const fire = () => transport.request(requestString, {
        onReady, onTimeout, onProgress, priority,
        sendAs: metMerk(requestString),
        fileKey: sleutel,
    });

    if (skipCache) { fire(); return; }

    const probe = new Image();
    probe.onload = () => {
        try { onReady && onReady(`${url}?t=${Date.now()}`, requestString); }
        catch (e) { console.error('[render] onReady error:', e); }
    };
    probe.onerror = fire;
    probe.src = url;
}

/**
 * Vraag stil een hogere kwaliteit aan en wissel het beeld om zodra die er is.
 * Komt hij niet, dan blijft de snelle preview gewoon staan.
 *
 * Zonder kwaliteits-tiers is dit een no-op: dan is de snelle render meteen de
 * definitieve.
 */
export function requestUpgrade(requestString, targetSelector, isStillCurrent) {
    if (!hasQualityTiers) return;
    requestRender(requestString, {
        priority: 5, // achtergrondwerk, wijkt voor previews waar iemand op wacht
        onReady: (url) => {
            if (isStillCurrent && !isStillCurrent()) return;
            document.querySelectorAll(targetSelector).forEach(t => { t.src = url; });
        },
        onTimeout: () => { /* geen HQ; de preview blijft staan */ },
    });
}

export function cancelRenders(keepStrings = []) {
    if (GEBLOKKEERD) return;
    transport.cancel(keepStrings);
}

export function warmupRenderer() {
    if (GEBLOKKEERD) return Promise.resolve();
    return transport.warmup();
}

export const transportName = transport.name;
